import React, { useState, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import WordCloud from "wordcloud";

import RemoteLayout from "./layout";
import { Conference, UserIcon } from "../../components/videoCalling";
import { WorkshopContext } from "../../contexts/workshop.context";
import { MultiSelectionPicker, SpeakingOrder } from "../../components/workshop";
import { defaultRoles } from "../../util/workshop.util";
import { getNextRoute, getPreviousRoute, setLocalStorage } from "../../util/general.util";
import { WordCloudRenderer } from "../../components/insights";
import { capitalizeFirstLetter } from "../../util/translations.util";

import demoDataSet, { demoUsers } from "../../util/demoData.util";

const TASK_ID = 6;

const colors = ["#3E3E3E", "#666666", "#E59536", "#BE6A03", "#EEB52B", "#F28907"];

const Task6 = ({ demoWorkshop = false }) => {
  const { t, i18n } = useTranslation();
  const [taskOrderUsers, setTaskOrderUsers] = useState([]);
  const [strengthsList, setStrengthsList] = useState([]);
  const [isStarted, setIsStarted] = useState(false);
  const [userSelections, setUserSelections] = useState([]);

  const Strengths = t("resources.strengths", { returnObjects: true });

  const {
    socket,
    user: { id, name, role } = {},
    workshopDetails: { platform } = {}
  } = useContext(WorkshopContext);

  const demoData = demoDataSet?.[platform]?.[`task${TASK_ID}`];

  // DEMO LOGIC
  useEffect(() => {
    if (demoWorkshop) {
      setTaskOrderUsers(demoUsers || []);
      handleDemoSelections([]); // initial
      return;
    }
    // --- ORIGINAL SOCKET LOGIC ---
    socket.emit("getTaskGeneralStatus", TASK_ID, ({ started }) => {
      if (started) setIsStarted(true);
    });

    socket?.on("taskGeneralStatusUpdated", (started) => {
      if (started) setIsStarted(true);
    });

    socket?.emit("taskOrder", TASK_ID, (users) => {
      if (users) setTaskOrderUsers(users);
    });

    socket?.on("strengths", (strengths) => {
      if (!Object.keys(strengths)?.length) return;

      renderWordCloud(strengths);

      return () => {
        // stop the renderring
        WordCloud.stop();
      };
    });

    socket?.on("selectedStrengths", (selectedStrengths) => {
      setUserSelections(selectedStrengths);
    });

    socket?.emit("strengths", id);
    // eslint-disable-next-line
  }, [id, demoWorkshop]);

  const getRandomColor = () => {
    return colors[Math.floor(Math.random() * colors.length)];
  };

  // DEMO: handle demo selections and word cloud
  const handleDemoSelections = (selected = []) => {
    setUserSelections(selected);
    const strengths = {};
    const currentDemoStrengths = demoData.predefinedDemoStrengths[i18n.language] || [];
    [...selected, ...currentDemoStrengths].forEach((strength) => {
      strengths[strength] = strengths[strength] ? strengths[strength] + 1 : 1;
    });
    renderWordCloud(strengths, true);
  };

  // DEMO: render word cloud
  const renderWordCloud = (strengths, isDemo = false) => {
    try {
      let increaseBy = 1;
      const wordsList = [];
      const originalWordsList = [];

      if (taskOrderUsers.length >= 2 && taskOrderUsers.length <= 5) increaseBy = 6;
      if (taskOrderUsers.length >= 6 && taskOrderUsers.length <= 12) increaseBy = 4;
      if (taskOrderUsers.length >= 13 && taskOrderUsers.length <= 18) increaseBy = 2.5;
      if (isDemo) increaseBy = 1;

      Object.keys(strengths).forEach((item) => {
        const tempStrength = isDemo ? strengths[item] : strengths[item] + 1;
        wordsList.push([capitalizeFirstLetter(item), tempStrength * increaseBy]);
        originalWordsList.push([capitalizeFirstLetter(item), strengths[item]]);
      });

      setStrengthsList(wordsList);
      if (isDemo) setLocalStorage("wordsList", originalWordsList);
    } catch (error) {
      console.log(error);
    }
  };

  // DEMO: navigation
  const handleBack = () => {
    const previousRoute = getPreviousRoute(`/task-6`, platform);
    socket.emit("navigate", previousRoute);
  };

  const handleNext = () => {
    const nextRoute = getNextRoute(`/task-6`, platform);
    if (demoWorkshop) {
      if (!isStarted) {
        setIsStarted(true);
        return;
      }
      setLocalStorage("strengthsSubmissions", {
        ...demoData.strengthsSubmissions,
        demo: userSelections
      });
      return socket.emit("navigate", nextRoute);
    }
    if (!isStarted) {
      socket.emit("updateTaskGeneralStatus", {
        taskId: TASK_ID,
        started: true
      });
      setIsStarted(true);
      return;
    }
    socket.emit("navigate", nextRoute);
  };

  // DEMO: update word cloud on user change
  useEffect(() => {
    if (demoWorkshop && taskOrderUsers.length > 0) {
      handleDemoSelections();
    }
    // eslint-disable-next-line
  }, [taskOrderUsers, demoWorkshop]);

  // UI
  const buttonText =
    role !== defaultRoles.participant && isStarted
      ? t("buttonNext") + " →"
      : role !== defaultRoles.participant && !isStarted
        ? t("buttonStart") + " →"
        : null;

  const handleButtonClick = () => {
    if (role !== defaultRoles.participant) {
      return handleNext;
    } else return null;
  };

  const showBackButton = role !== defaultRoles.participant && !isStarted;

  return (
    <RemoteLayout
      buttonText={buttonText}
      onButtonClick={handleButtonClick()}
      showParticipants={true}
      onBackClick={showBackButton && handleBack}>
      <div className="text-center mb-8">
        <h1 className="text-white text-2xl md:text-4xl flex items-center justify-center gap-2 text-white-100">
          <span className="text-yellow-500">🏆</span>{" "}
          {isStarted ? t("task6.started.title") : t("task6.title")}
        </h1>
        <p
          className="mt-4 max-w-2xl text-lg mx-auto text-white-100"
          dangerouslySetInnerHTML={{
            __html: isStarted ? t("task6.started.description") : t("task6.description")
          }}
        />
      </div>

      {/* Main Content */}
      {isStarted ? (
        <>
          <div className=" mx-auto flex items-center justify-center mb-6">
            <SpeakingOrder participants={taskOrderUsers} filterOnlineParticipants={true} />
          </div>
          <div
            className={`flex flex-col md:flex-row md:items-center gap-4 w-full ${role === defaultRoles.moderator && "justify-center"}`}>
            {role !== defaultRoles.moderator && (
              <div className="w-3/12 hidden md:block">
                <div className="w-full">
                  <div className="mt-4">
                    <MultiSelectionPicker
                      socket={socket}
                      moderator={role === defaultRoles.moderator}
                      userId={id}
                      updateId={"updateStrengths"}
                      submitAllTogether={true}
                      disableSelection={userSelections?.length > 0}
                      dummySelections={userSelections || []}
                      title={t("mtParticipantActivityScreen.dropdownOne")}
                      options={Strengths?.length > 0 && Strengths?.map((s) => s?.title)}
                      customClasses={"!bg-orange-100 !rounded-full !border-orange-100"}
                      selectionTitle={t("task6.selections.title")}
                      textClasses={"text-white-100"}
                      onChange={demoWorkshop ? handleDemoSelections : undefined}
                    />
                  </div>
                </div>
              </div>
            )}
            <div className="w-full md:w-9/12">
              {role !== defaultRoles.moderator && (
                <div className="my-4 md:hidden">
                  <MultiSelectionPicker
                    socket={socket}
                    moderator={role === defaultRoles.moderator}
                    userId={id}
                    updateId={"updateStrengths"}
                    submitAllTogether={true}
                    disableSelection={userSelections?.length > 0}
                    dummySelections={userSelections || []}
                    title={t("mtParticipantActivityScreen.dropdownOne")}
                    options={Strengths?.length > 0 && Strengths?.map((s) => s?.title)}
                    customClasses={"!bg-orange-100 !rounded-full !border-orange-100"}
                    selectionTitle={t("task6.selections.title")}
                    textClasses={"text-white-100"}
                    onChange={demoWorkshop ? handleDemoSelections : undefined}
                  />
                </div>
              )}

              <WordCloudRenderer
                words={strengthsList}
                width={1200}
                height={1200}
                colorFunction={getRandomColor}
                weightFactorFormula={
                  demoWorkshop
                    ? undefined
                    : (size) => {
                        const maxSize = 250;
                        const calculatedSize = Math.pow(size, 5.5);
                        return Math.min(calculatedSize, maxSize);
                      }
                }
                showEmpty={true}
              />
            </div>
          </div>

          <div className="overflow-y-auto mt-4 w-full">
            <Conference isLinear={true} demoWorkshop={demoWorkshop} />
          </div>
        </>
      ) : (
        <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 mb-12 w-full`}>
          {/* Task Overview Card */}
          <div className="bg-orange-50 text-black p-6 rounded-2xl w-full shadow-2xl col-span-1 h-fit">
            <h2 className="text-orange-default font-bold text-xl mb-4">{t("task6.card.title")}</h2>
            <ul
              className="list-inside"
              dangerouslySetInnerHTML={{ __html: t("task6.card.list.item1") }}
            />
          </div>
          <div className="col-span-1 md:col-span-2 w-full">
            {/* Participants Grid */}
            <Conference smallSpace={true} demoWorkshop={demoWorkshop} />
          </div>
        </div>
      )}
    </RemoteLayout>
  );
};

export default Task6;
